import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "./auth.service";
import { SessaoService } from "./sessao.service";

/**
 * PROSP-594 (companion front): encerra a sessao apos N de INATIVIDADE.
 *
 * Camada de UX/cliente do idle-timeout: detecta ausencia de interacao do usuario
 * na aba aberta (mouse/teclado/scroll/touch) e, ao estourar a janela, faz
 * logout + redireciona pro login — SEM depender de chamada de rede (logo, nao e
 * mascarado por cache). Complementa a camada de servidor (middleware
 * EnforceIdleTimeout), que pega o caso de reabrir o app depois de inativo.
 *
 * A janela e CONFIGURAVEL: a fonte da verdade e o backend (parametro
 * IDLE_TIMEOUT_MINUTES, editavel por um admin). O valor e buscado no boot e no
 * login e revalidado periodicamente, para alcancar sessoes ja abertas quando um
 * admin muda o tempo. Se a busca falhar, mantem-se o ultimo valido / o default
 * de 4h — nunca deixa de proteger.
 *
 * A "ultima atividade" e persistida no localStorage (com fallback em memoria se
 * o storage estiver indisponivel), entao fechar/reabrir a aba dentro da janela
 * mantem a sessao; alem da janela, desloga ao carregar.
 */
@Injectable({ providedIn: "root" })
export class IdleTimeoutService implements OnDestroy {
    /** Janela padrao (fallback): 4 horas. Usada ate o backend responder e se a
     *  busca da config falhar. */
    static readonly DEFAULT_IDLE_TIMEOUT_MS = 4 * 60 * 60 * 1000;

    /** Limites aceitos, espelhando o backend (5 min .. 24h). Config fora disso
     *  cai no default — defesa contra valor absurdo vindo por qualquer via. */
    private static readonly MIN_TIMEOUT_MS = 5 * 60 * 1000;
    private static readonly MAX_TIMEOUT_MS = 24 * 60 * 60 * 1000;

    /** Revalida a config periodicamente (alcanca sessoes ja abertas). */
    private static readonly CONFIG_POLL_MS = 15 * 60 * 1000;

    /** Throttle de escrita no localStorage (evita gravar a cada mousemove). */
    private static readonly ACTIVITY_THROTTLE_MS = 30 * 1000;

    private static readonly LAST_ACTIVITY_KEY = "last_activity_at";

    private static readonly ACTIVITY_EVENTS = [
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
        "wheel",
        "click",
    ];

    /** Janela EFETIVA (inicia no default; ajustada pela config do backend). */
    private idleTimeoutMs = IdleTimeoutService.DEFAULT_IDLE_TIMEOUT_MS;

    private timerId: any = null;
    private configPollId: any = null;
    private lastWrite = 0;
    /** Fallback em memoria da ultima atividade, para quando o localStorage
     *  estiver indisponivel (modo anonimo estrito / storage desabilitado). */
    private lastActivityMem: number | null = null;
    private started = false;
    private readonly boundActivity = (): void => this.onActivity();

    constructor(
        private authService: AuthService,
        private router: Router,
        private zone: NgZone,
        private sessaoService: SessaoService
    ) {}

    /** Inicia o monitoramento de inatividade (idempotente). */
    start(): void {
        if (this.started) {
            return;
        }
        this.started = true;

        // Ja estava inativo alem do limite ao carregar/voltar -> desloga.
        // Checagem inicial com a janela default; refreshConfig() reavalia com a
        // janela REAL assim que o backend responde (pode expirar depois disso).
        if (this.authService.isAuthenticated() && this.isExpired()) {
            this.expire();
            return;
        }

        // Listeners fora do Angular: nao dispara change detection a cada evento.
        this.zone.runOutsideAngular(() => {
            IdleTimeoutService.ACTIVITY_EVENTS.forEach((evt) =>
                window.addEventListener(evt, this.boundActivity, { passive: true })
            );
        });

        if (this.authService.isAuthenticated()) {
            // NAO sobrescreve a inatividade acumulada: se ja existe marca (sessao
            // pre-existente), preserva-a e agenda com base nela; so semeia quando
            // NAO ha marca (1a vez / legado). Sobrescrever aqui — antes de saber a
            // janela real — mascarava expiracoes: voltar apos 2h com janela de
            // 30min pareceria "recem-ativo". A reavaliacao correta ocorre em
            // refreshConfig(), com a marca preservada e a janela do backend.
            if (this.readLastActivity() === null) {
                this.markActivity(true);
            } else {
                this.scheduleCheck();
            }
            this.refreshConfig(); // busca a janela real e reavalia (pode expirar)
        }

        this.startConfigPolling(); // revalida periodicamente
    }

    /**
     * Chamado pelos finalizadores de login (token/SSO): semeia a atividade e
     * busca a janela efetiva imediatamente, para a sessao recem-aberta ja
     * respeitar o valor configurado (e nao o default por ate um ciclo de poll).
     */
    onLogin(): void {
        if (!this.started) {
            this.start();
            return;
        }
        if (this.authService.isAuthenticated()) {
            this.markActivity(true);
            this.refreshConfig();
        }
    }

    /**
     * Busca a janela efetiva no backend e reprograma. Em falha, mantem a janela
     * atual (ultimo valido / default) — o timeout nunca deixa de valer.
     */
    refreshConfig(): void {
        if (!this.authService.isAuthenticated()) {
            return;
        }

        this.sessaoService.getIdleTimeout().subscribe({
            next: (resp: any) => {
                const minutes = Number(resp?.data?.minutes);
                if (!Number.isFinite(minutes) || minutes <= 0) {
                    return; // resposta estranha -> mantem a janela atual
                }

                const ms = minutes * 60 * 1000;
                this.idleTimeoutMs = Math.min(
                    IdleTimeoutService.MAX_TIMEOUT_MS,
                    Math.max(IdleTimeoutService.MIN_TIMEOUT_MS, ms)
                );

                // Reprograma com a janela nova. Se reduziram o tempo e a sessao
                // ja passou do novo limite, expira agora.
                if (this.started && this.authService.isAuthenticated()) {
                    if (this.isExpired()) {
                        this.zone.run(() => this.expire());
                    } else {
                        this.scheduleCheck();
                    }
                }
            },
            error: () => {
                /* mantem a janela atual (default / ultimo valido) */
            },
        });
    }

    private startConfigPolling(): void {
        if (this.configPollId) {
            return;
        }
        this.zone.runOutsideAngular(() => {
            this.configPollId = setInterval(
                () => this.refreshConfig(),
                IdleTimeoutService.CONFIG_POLL_MS
            );
        });
    }

    stop(): void {
        this.started = false;
        IdleTimeoutService.ACTIVITY_EVENTS.forEach((evt) =>
            window.removeEventListener(evt, this.boundActivity)
        );
        if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
        if (this.configPollId) {
            clearInterval(this.configPollId);
            this.configPollId = null;
        }
    }

    ngOnDestroy(): void {
        this.stop();
    }

    /** Decisao pura de expiracao por inatividade (testavel). */
    static isIdleExpired(lastActivity: number | null, now: number, timeoutMs: number): boolean {
        if (lastActivity === null || isNaN(lastActivity)) {
            return false; // sem marca (1a vez / legado) -> nao expira
        }
        return now - lastActivity > timeoutMs;
    }

    private onActivity(): void {
        // Nao rastreia atividade quando deslogado (ex.: tela de login).
        if (this.authService.isAuthenticated()) {
            this.markActivity(false);
        }
    }

    /** Grava a marca de atividade (com throttle) e reagenda a verificacao. */
    private markActivity(force: boolean): void {
        const now = Date.now();
        if (force || now - this.lastWrite >= IdleTimeoutService.ACTIVITY_THROTTLE_MS) {
            this.lastWrite = now;
            // Fallback em memoria SEMPRE atualizado (vale mesmo sem storage).
            this.lastActivityMem = now;
            try {
                window.localStorage.setItem(IdleTimeoutService.LAST_ACTIVITY_KEY, String(now));
            } catch {
                /* storage indisponivel: segue com o fallback em memoria acima */
            }
            this.scheduleCheck();
        }
    }

    private scheduleCheck(): void {
        if (this.timerId) {
            clearTimeout(this.timerId);
        }
        // Agenda pelo tempo RESTANTE da janela (com base na ultima atividade),
        // nao sempre a janela cheia — preciso mesmo apos checagens intermediarias.
        const last = this.readLastActivity();
        const elapsed = last === null ? 0 : Date.now() - last;
        const remaining = Math.max(0, this.idleTimeoutMs - elapsed);
        this.zone.runOutsideAngular(() => {
            this.timerId = setTimeout(() => this.check(), remaining);
        });
    }

    private check(): void {
        // Deslogado (ex.: tela de login): nao reagenda -> evita timer perpetuo.
        if (!this.authService.isAuthenticated()) {
            return;
        }
        if (this.isExpired()) {
            this.zone.run(() => this.expire());
        } else {
            this.scheduleCheck();
        }
    }

    private isExpired(): boolean {
        return IdleTimeoutService.isIdleExpired(
            this.readLastActivity(),
            Date.now(),
            this.idleTimeoutMs
        );
    }

    /**
     * Le a marca de atividade. Preferencia pelo localStorage (persiste entre
     * abas/recarregar); em indisponibilidade (SecurityError/DOMException em modo
     * anonimo estrito ou storage desabilitado) ou ausencia da chave, cai no
     * fallback EM MEMORIA. Antes, sem esse fallback, o storage indisponivel
     * fazia readLastActivity() sempre retornar null -> isExpired() nunca dava
     * true -> o logout por inatividade do cliente jamais disparava.
     */
    private readLastActivity(): number | null {
        try {
            const raw = window.localStorage.getItem(IdleTimeoutService.LAST_ACTIVITY_KEY);
            if (raw === null) {
                return this.lastActivityMem;
            }
            const parsed = parseInt(raw, 10);
            return isNaN(parsed) ? this.lastActivityMem : parsed;
        } catch {
            return this.lastActivityMem;
        }
    }

    private expire(): void {
        this.stop();
        this.lastActivityMem = null;
        try {
            window.localStorage.removeItem(IdleTimeoutService.LAST_ACTIVITY_KEY);
        } catch {
            /* ignore */
        }
        this.authService.logout();
        this.router.navigate(["/auth/login"]);
    }
}
